import { setClassState, unsetClassState } from './element-manipulation'
import { timer } from './timer'

export default function createTabGroup ({
  tabs, folders, folderSet, heightOffset, closeButtons, hover,
}) {
  let openFolder = null
  const openFolderChanged = []
  const timerCloseReset = timer()
  const timerChange = timer()
  const timerOpenReset = timer()
  const timerMouseleave = timer()
  const folderFromTab = tab => folders.filter(folder => folder.dataset.folder === tab.dataset.folder)[0]
  const tabFromFolder = folder => tabs.filter(tab => tab.dataset.folder === folder.dataset.folder)[0]
  const folderById = id => folders.filter(folder => id === folder.dataset.folder)[0]
  const close = (folder, toOpenAnother) => {
    const tab = tabFromFolder(folder)
    if (tab) {
      tab.setAttribute('aria-selected', 'false')
      setClassState(tab, tab.dataset.classInactive)
      unsetClassState(tab, tab.dataset.classActive)
    }
    folder.setAttribute('aria-expanded', 'false')
    folder.setAttribute('aria-live', 'off')
    if (folderSet) {
      if (!toOpenAnother) {
        folderSet.style.height = `${folder.clientHeight + heightOffset}px`
        folderSet.dataset.changing = '1'
      }
    }
    timerCloseReset.start(() => {
      if (toOpenAnother) {
        setClassState(folder, folder.dataset.classInactive)
        unsetClassState(folder, folder.dataset.classActive)
      } else if (folderSet) {
        folderSet.style.height = '0'
      }
    }, 5)
    if (!toOpenAnother) {
      timerChange.start(() => {
        openFolder = null
        if (folderSet) {
          folderSet.dataset.changing = '0'
          setClassState(folderSet, folderSet.dataset.classInactive)
          unsetClassState(folderSet, folderSet.dataset.classActive)
        }
        setClassState(folder, folder.dataset.classInactive)
        unsetClassState(folder, folder.dataset.classActive)
        tabs.forEach(tab => tab.dataset.anyTabsExpanded = '1')
      }, 310)
    }
    openFolderChanged.forEach(listener => listener({ sender: tabGroup, tab, folder }))
  }
  const open = (folder, previouslyOpenFolder) => {
    const tab = tabFromFolder(folder)
    if (tab) {
      tab.setAttribute('aria-selected', 'true')
      setClassState(tab, tab.dataset.classActive)
      unsetClassState(tab, tab.dataset.classInactive)
    }
    folder.setAttribute('aria-expanded', 'true')
    folder.setAttribute('aria-live', 'polite')
    if (folderSet) {
      folderSet.dataset.changing = '1'
      setClassState(folderSet, folderSet.dataset.classActive)
      unsetClassState(folderSet, folderSet.dataset.classInactive)
    }
    setClassState(folder, folder.dataset.classActive)
    unsetClassState(folder, folder.dataset.classInactive)
    if (previouslyOpenFolder) {
      if (folderSet) {
        folderSet.style.height = `${previouslyOpenFolder.clientHeight + heightOffset}px`
      }
    } else {
      tabs.forEach(tab => tab.dataset.anyTabsExpanded = '0')
    }
    timerOpenReset.start(() => {
      if (folderSet) {
        folderSet.style.height = `${folder.clientHeight + heightOffset}px`
      }
    }, 5)
    timerChange.start(() => {
      if (folderSet) {
        folderSet.dataset.changing = '0'
        folderSet.style.height = 'auto'
      }
      openFolder = folder
    }, 310)
    openFolderChanged.forEach(listener => listener({ sender: tabGroup, tab, folder }))
  }
  const closeCurrentlyOpen = () => {
    if (openFolder) {
      close(openFolder)
    }
  }
  const getTabInDirection = (tab, direction) => {
    let tabIndex = tabs.indexOf(tab) + direction
    if (tabIndex >= tabs.length) {
      tabIndex = 0
    } else if (tabIndex < 0) {
      tabIndex = tabs.length - 1
    }
    return tabs[tabIndex]
  }
  const openTab = (tab) => {
    timerCloseReset.runAndClear()
    timerOpenReset.runAndClear()
    timerChange.runAndClear()
    const folder = folderFromTab(tab)
    if (openFolder === null) {
      open(folder, openFolder)
    } else if (openFolder !== folder) {
      close(openFolder, true)
      open(folder, openFolder)
    } else if (!hover && (openFolder === folder)) {
      close(folder)
    }
  }
  if (folderSet) {
    folderSet.dataset.changing = '0'
    setClassState(folderSet, folderSet.dataset.classInactive)
    unsetClassState(folderSet, folderSet.dataset.classActive)
  }
  closeButtons.forEach((closeButton) => {
    closeButton.addEventListener('click', closeCurrentlyOpen)
  })
  folders.forEach((folder) => {
    folder.id = `megamenu-folder-${folder.dataset.folder}`
  })
  tabs.forEach((tab) => {
    tab.addEventListener('keydown', (event) => {
      if (event.which === 37) {
        const targetTab = getTabInDirection(tab, -1)
        targetTab.focus()
        if (openFolder) {
          openTab(targetTab)
        }
        document.body.classList.add('keyboard-navigation')
      } else if (event.which === 39) {
        const targetTab = getTabInDirection(tab, 1)
        targetTab.focus()
        if (openFolder) {
          openTab(targetTab)
        }
        document.body.classList.add('keyboard-navigation')
      }
    })
    if (hover) {
      const folder = folderFromTab(tab)
      const closeFolder = () => {
        close(folder)
      }
      tab.addEventListener('mouseenter', () => {
        timerMouseleave.cancel()
        openTab(tab)
      })
      tab.addEventListener('mouseleave', (event) => {
        timerMouseleave.start(closeFolder, 10)
      })
      folder.addEventListener('mouseenter', (event) => {
        timerMouseleave.cancel()
      })
      folder.addEventListener('mouseleave', (event) => {
        timerMouseleave.start(closeFolder, 10)
      })
    } else {
      tab.addEventListener('click', () => {
        openTab(tab)
      })
    }
  })
  const openById = (id) => {
    const folder = folderById(id)
    if (openFolder === null) {
      open(folder, openFolder)
    } else if (openFolder !== folder) {
      close(openFolder, true)
      open(folder, openFolder)
    }
  }
  const tabGroup = {
    openById,
    open,
    close,
    closeCurrentlyOpen,
    openFolderChanged,
  }
  return tabGroup
}
